import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button, CheckBox, Panel } from '../../../atoms'
import SvgIcon from 'components/atoms/SvgIcon'
import { EditTable } from '../../../organisms'
import SidebarLayout from '../../_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IProps {
  agent_ranks: IJsonResponse
  user: any
}

const AgentRanksIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Agent ranks')
  const { data } = agentService.getDataFromJson(props.agent_ranks)
  const { data: userData } = utilService.getDataFromJson(props.user)
  const [agentRanks, setAgentRanks] = React.useState(data)

  const handleEnabledChange = React.useCallback(async (record, event) => {
    const target = event.target
    const enabled = target.checked
    try {
      const { flush } = await agentService.updateAgentRank({ id: record.id, enabled })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      // 更新に失敗した場合はチェック状態を元に戻す（エラー表示はhttpClientのinterceptorが行う）
      target.checked = !enabled
    }
  }, [])

  const columns = [
    {
      name: I18n.t('generic.name'),
      field: 'name',
    },
    {
      name: I18n.t('agent_rank.kind'),
      field: 'rank_kind',
      renderElement: record => (
        <span>
          {record.rank_kind === 'special'
            ? I18n.t('agent_rank.kind_special')
            : I18n.t('agent_rank.kind_normal')}
        </span>
      ),
    },
    {
      name: I18n.t('agent_rank.base'),
      field: 'is_base',
      renderElement: record =>
        record.is_base ? (
          <SvgIcon
            name="check"
            color={constants.COLORS.primary}
            width={20}
            height={20}
            ariaHidden={true}
            role="presentation"
          />
        ) : null,
    },
    {
      name: I18n.t('generic.position'),
      field: 'position',
    },
    {
      name: I18n.t('agent_rank.enabled'),
      field: 'enabled',
      renderElement: record => (
        <CheckBox
          name={`enabled_${record.id}`}
          defaultChecked={record.enabled}
          onChangeHandler={event => handleEnabledChange(record, event)}
        />
      ),
    },
  ]

  const handleDelete = React.useCallback(
    async id => {
      if (!window.confirm(I18n.t('agent_rank.delete_confirmation'))) {
        return
      }

      try {
        const { flush } = await agentService.deleteAgentRank(id)
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
        setAgentRanks(agentRanks.filter(agentRank => agentRank.id !== id))
      } catch (error) {
        // ガイド設定済み等で削除できない場合の422はhttpClientのinterceptorがflashを表示するため、行を残すだけでよい
      }
    },
    [agentRanks]
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <Heading>
              <span>{I18n.t('generic.rank')}</span>
              <Button small={true}>
                <a href="/agent/agent_ranks/new">{I18n.t('generic.create_new')}</a>
              </Button>
            </Heading>
          }
        >
          <Note>{I18n.t('agent_rank.note')}</Note>
          <EditTable
            editable={true}
            columns={columns}
            records={agentRanks}
            handleDelete={handleDelete}
            getEditLink={({ id }) => `/agent/agent_ranks/${id}/edit`}
          />
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  /* 操作列は編集・削除の順に並ぶため、末尾の削除ボタンを警告色にする */
  .Table_Data._buttons .Button:last-child {
    color: ${constants.COLORS.caution};
    border-color: ${constants.COLORS.caution};
  }
`

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

const Note = styled.div`
  font-size: 12px;
  margin-bottom: 12px;
  color: ${constants.COLORS.subText};
`

export default AgentRanksIndex
